import { HTMLAttributes, useEffect, useState } from 'react';
import { usePolicyStore } from '@/dashboard/store/PolicyStore';
import {
  BriefcaseIcon,
  TruckIcon,
  UserCircleIcon,
} from '@heroicons/react/solid';
import { GridItem, JoveButton } from '@jove-frontend/mui';
import { PROVIDER_SOURCE, openUrlWithNewWindow } from '@jove-frontend/uikit';
import { Box, Grid, Typography } from '@mui/material';
import clsx from 'clsx';

type HomeProductIntroductionViewProps = HTMLAttributes<HTMLDivElement>;
const HomeProductIntroductionView = (
  props: HomeProductIntroductionViewProps
) => {
  const { className } = props;
  const {
    state: { policies },
  } = usePolicyStore();
  const [cardList, setCardList] = useState([]);

  useEffect(() => {
    if (policies?.length > 0) {
      const list = [];
      if (
        !policies.find((item) =>
          [
            PROVIDER_SOURCE().ACCELERANT.value,
            PROVIDER_SOURCE().CHUBB.value,
          ].includes(item.providerCode)
        )
      ) {
        list.push({
          title: 'Business Insurance',
          content: `Protect your business with Jove's comprehensive Business Insurance! Our policies are designed to safeguard your assets and ensure peace of mind. Ready to find the perfect coverage?`,
          key: 'businessInsurance',
          tagText: '',
        });
      }
      if (
        !policies.find(
          (item) => item.providerCode === PROVIDER_SOURCE().GREATEAMERICAN.value
        )
      ) {
        list.push({
          title: 'Motor Truck Cargo insurance',
          content: `Secure your shipments with Jove's Motor Truck Cargo insurance! Our comprehensive coverage is designed to protect your delicate and high-value items against unforeseen circumstances.`,
          key: 'motorTruckCargoInsurance',
          tagText: '',
        });
      }
      list.push({
        title: 'Bespoke Insurance Brokerage',
        content: `Jove customizes your insurance policy with Bespoke Insurance Brokerage. Our team is ready to assist you with tailored coverage and personal service. Learn how we can help you!`,
        key: 'bespokeInsuranceBrokerage',
        tagText: '',
      });
      setCardList(list);
    }
  }, [policies]);

  /**
   * methods
   */
  const getQuoteAction = () => {
    openUrlWithNewWindow(process.env.NX_QUOTE_ENDPOINT);
  };

  const contactUsAction = () => {
    openUrlWithNewWindow(`${process.env.NX_WEBSITE_URL}/contact-us`);
  };

  if (cardList.length === 0) {
    return null;
  }

  return (
    <Box className={clsx('flex w-full flex-col space-y-1', className)}>
      <Typography variant="h5" className="font-black">
        For you
      </Typography>
      <Grid container spacing={2}>
        {cardList.map((item) => {
          return (
            <Grid item key={item.key} xs={6} md={4}>
              <GridItem>
                <Box className="border-grey-20 relative flex flex-col space-y-4 rounded-lg border p-6">
                  {!!item.tagText && (
                    <Box className="bg-brand-accent absolute right-0 top-0 rounded-bl-lg rounded-tr-lg px-[8px] py-[6px]">
                      <Typography
                        variant="subtitle2"
                        className="text-xs font-bold"
                      >
                        {item.tagText}
                      </Typography>
                    </Box>
                  )}
                  <Box className="bg-light-purple flex h-14 w-14 flex-col items-center justify-center rounded-full">
                    {'businessInsurance' === item.key && (
                      <BriefcaseIcon className="text-brand-secondary h-7 w-7" />
                    )}
                    {'motorTruckCargoInsurance' === item.key && (
                      <TruckIcon className="text-brand-secondary h-7 w-7" />
                    )}
                    {'bespokeInsuranceBrokerage' === item.key && (
                      <UserCircleIcon className="text-brand-secondary h-8 w-8" />
                    )}
                  </Box>
                  <Box className="flex flex-col">
                    <Typography variant="h6" className="font-black">
                      {item.title}
                    </Typography>
                    <Typography variant="subtitle2" className="font-medium">
                      {item.content}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col items-start">
                    {['businessInsurance', 'motorTruckCargoInsurance'].includes(
                      item.key
                    ) && (
                      <JoveButton onClick={getQuoteAction}>
                        Get a quote
                      </JoveButton>
                    )}
                    {['bespokeInsuranceBrokerage'].includes(item.key) && (
                      <JoveButton onClick={contactUsAction}>
                        Get in contact
                      </JoveButton>
                    )}
                  </Box>
                </Box>
              </GridItem>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default HomeProductIntroductionView;
